import React, { useEffect, useRef, useState } from 'react';
import { components, MenuListProps } from 'react-select';
import { KeyModel } from '../services/AppConfig/types';

/** Tiempo que se acumulan las pulsaciones antes de empezar una búsqueda nueva. */
const TYPEAHEAD_RESET_MS = 1000;

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/**
 * MenuList que mantiene a la vista la opción seleccionada, para que al teclear
 * la lista se desplace hasta ella en vez de quedarse donde estaba.
 */
const ScrollToSelectedMenuList = (props: MenuListProps<KeyModel, false>) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const selectedCode = props.getValue()[0]?.code;

  useEffect(() => {
    const selectedOption = listRef.current?.querySelector<HTMLElement>(
      '.select__option--is-selected',
    );
    selectedOption?.scrollIntoView({ block: 'nearest' });
  }, [selectedCode]);

  return (
    <components.MenuList
      {...props}
      innerRef={(element: HTMLDivElement) => {
        listRef.current = element;
        if (typeof props.innerRef === 'function') props.innerRef(element);
      }}
    >
      {props.children}
    </components.MenuList>
  );
};

// Permite filtrar lista de opciones de un input select por texto tecelado
export const useSelectTypeahead = () => {
  const search = useRef<string>('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Al elegir una opción react-select devuelve el foco al control y
  // openMenuOnFocus reabriría el menú: esto lo evita una sola vez.
  const ignoreNextOpen = useRef<boolean>(false);
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const resetSearchAfterPause = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      search.current = '';
    }, TYPEAHEAD_RESET_MS);
  };

  return (
    options: KeyModel[] | undefined,
    selectedCode: string | undefined,
    onSelect: (code: string) => void,
  ) => {
    const selectByOffset = (offset: number) => {
      if (!options?.length) return;
      const currentIndex = options.findIndex(
        (option) => option.code === selectedCode,
      );
      const nextIndex = Math.min(
        Math.max(currentIndex + offset, 0),
        options.length - 1,
      );
      if (nextIndex !== currentIndex) onSelect(options[nextIndex].code);
    };

    const selectByTypedText = (typedKey: string) => {
      if (!options?.length) return;
      const key = normalize(typedKey);
      const isSameKeyRepeated = search.current === key;
      search.current = isSameKeyRepeated ? key : search.current + key;
      resetSearchAfterPause();

      const matches = options.filter((option) =>
        normalize(option.description_code).startsWith(search.current),
      );
      if (!matches.length) return;

      // Repetir la misma letra rota entre las opciones que empiezan por ella.
      let match = matches[0];
      if (isSameKeyRepeated) {
        const currentIndex = matches.findIndex(
          (option) => option.code === selectedCode,
        );
        match = matches[(currentIndex + 1) % matches.length];
      }

      if (match.code !== selectedCode) onSelect(match.code);
    };

    const closeMenu = () => {
      ignoreNextOpen.current = true;
      setMenuIsOpen(false);
      setTimeout(() => {
        ignoreNextOpen.current = false;
      });
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.ctrlKey || event.altKey || event.metaKey) return;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          // Con el menú cerrado dejamos que react-select lo abra.
          if (!menuIsOpen) return;
          event.preventDefault();
          selectByOffset(event.key === 'ArrowDown' ? 1 : -1);
          return;
        case 'Enter':
          // Sin menú abierto, Enter debe seguir enviando el formulario.
          if (!menuIsOpen) return;
          event.preventDefault();
          closeMenu();
          return;
        case 'Escape':
          if (!menuIsOpen) return;
          event.preventDefault();
          closeMenu();
          return;
        case ' ':
          event.preventDefault();
          // Si ya se está escribiendo, el espacio forma parte de la búsqueda
          // ("Costa Rica", "Reino Unido"); si no, abre o cierra el menú.
          if (search.current) selectByTypedText(event.key);
          else setMenuIsOpen(!menuIsOpen);
          return;
        default:
          // Solo caracteres imprimibles.
          if (event.key.length !== 1) return;
          event.preventDefault();
          selectByTypedText(event.key);
      }
    };

    return {
      components: { MenuList: ScrollToSelectedMenuList },
      menuIsOpen,
      onChange: (option: KeyModel | null) => {
        if (option?.code) onSelect(option.code);
        closeMenu();
      },
      onMenuOpen: () => {
        if (ignoreNextOpen.current) return;
        setMenuIsOpen(true);
      },
      onMenuClose: () => setMenuIsOpen(false),
      onKeyDown: handleKeyDown,
      // Al tabular se cerraría confirmando la opción interna de react-select.
      tabSelectsValue: false,
    };
  };
};
